import { useEffect, useState } from 'react';
import {
  AppVersionConfigEntity,
  Platform,
  useGetAppVersionConfigsQuery,
  useUpsertAppVersionConfigMutation,
} from 'src/store/appVersionApi';
import { Alert } from 'src/ui/Alert';
import { Button } from 'src/ui/Button';
import { EmptyState } from 'src/ui/EmptyState';
import { Field } from 'src/ui/Field';
import { Icon } from 'src/ui/Icon';
import { Input } from 'src/ui/Input';
import { LoadingBlock } from 'src/ui/Spinner';
import { Switch } from 'src/ui/Switch';
import { Textarea } from 'src/ui/Textarea';
import { useToast } from 'src/ui/Toast';
import { PageHead } from 'src/ui/patterns/PageHead';
// Editable defaults — update appVersionDefaults.json over time and click
// "Load from constants" on a card to populate the form from it.
import appVersionDefaults from './appVersionDefaults.json';

interface FormState {
  latestVersion: string;
  minimumVersion: string;
  storeUrl: string;
  releaseNotes: string;
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  latestVersion: '',
  minimumVersion: '',
  storeUrl: '',
  releaseNotes: '',
  enabled: true,
};

const PLATFORM_LABEL: Record<Platform, string> = {
  [Platform.IOS]: 'iOS',
  [Platform.ANDROID]: 'Android',
};

const PLATFORM_STORE: Record<Platform, string> = {
  [Platform.IOS]: 'App Store',
  [Platform.ANDROID]: 'Play Store',
};

const DEFAULTS_KEY: Record<Platform, keyof typeof appVersionDefaults> = {
  [Platform.IOS]: 'ios',
  [Platform.ANDROID]: 'android',
};

const defaultsFor = (platform: Platform): FormState => {
  const d = appVersionDefaults[DEFAULTS_KEY[platform]];
  return {
    latestVersion: d.latestVersion ?? '',
    minimumVersion: d.minimumVersion ?? '',
    storeUrl: d.storeUrl ?? '',
    releaseNotes: d.releaseNotes ?? '',
    enabled: d.enabled ?? true,
  };
};

const SEMVER = /^\d+\.\d+\.\d+$/;
const validSemver = (v: string) => SEMVER.test((v || '').trim());

interface PlatformCardProps {
  platform: Platform;
  config?: AppVersionConfigEntity;
}

const PlatformCard = ({ platform, config }: PlatformCardProps) => {
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<
    Partial<Record<keyof FormState, boolean>>
  >({});
  const [upsert, { isLoading }] = useUpsertAppVersionConfigMutation();

  useEffect(() => {
    if (config) {
      setForm({
        latestVersion: config.latestVersion ?? '',
        minimumVersion: config.minimumVersion ?? '',
        storeUrl: config.storeUrl ?? '',
        releaseNotes: config.releaseNotes ?? '',
        enabled: config.enabled,
      });
    }
  }, [config]);

  const set = (key: keyof FormState, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const touch = (key: keyof FormState) =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const latestErr =
    touched.latestVersion &&
    form.latestVersion &&
    !validSemver(form.latestVersion)
      ? 'Use a semantic version like 2.2.0'
      : '';
  const minErr =
    touched.minimumVersion &&
    form.minimumVersion &&
    !validSemver(form.minimumVersion)
      ? 'Use a semantic version like 2.0.0'
      : '';

  const onLoadDefaults = () => {
    setForm(defaultsFor(platform));
    setTouched({});
    toast({
      type: 'info',
      title: 'Loaded from constants',
      message: `${PLATFORM_LABEL[platform]} values reset to backend defaults.`,
    });
  };

  const onSave = async () => {
    setTouched({ latestVersion: true, minimumVersion: true });
    if (
      (form.latestVersion && !validSemver(form.latestVersion)) ||
      (form.minimumVersion && !validSemver(form.minimumVersion))
    ) {
      toast({
        type: 'error',
        title: 'Fix validation errors',
        message: 'Versions must be valid semver.',
      });
      return;
    }
    try {
      await upsert({
        platform,
        latestVersion: form.latestVersion || undefined,
        minimumVersion: form.minimumVersion || undefined,
        storeUrl: form.storeUrl || undefined,
        releaseNotes: form.releaseNotes || undefined,
        enabled: form.enabled,
      }).unwrap();
      toast({
        type: 'success',
        title: `${PLATFORM_LABEL[platform]} prompt saved`,
        message: `Latest ${form.latestVersion} · min ${form.minimumVersion}`,
      });
    } catch (err: any) {
      const message = err?.data?.message || err?.message || 'Save failed';
      toast({
        type: 'error',
        title: 'Save failed',
        message: Array.isArray(message) ? message.join(', ') : message,
      });
    }
  };

  return (
    <div
      className="panel"
      style={{
        flex: '1 1 380px',
        minWidth: 320,
        maxWidth: 560,
        opacity: form.enabled ? 1 : 0.72,
        transition: 'opacity .15s',
      }}
    >
      {/* Panel header: platform icon + name + store + enabled toggle */}
      <div className="panel-header">
        <div className="row" style={{ gap: 11, width: '100%' }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'var(--text-secondary)',
            }}
          >
            <Icon name="phone" size={18} />
          </div>
          <div className="grow" style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>
              {PLATFORM_LABEL[platform]}
            </div>
            <div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              {PLATFORM_STORE[platform]}
            </div>
          </div>
          <label
            className="row gap-2"
            style={{ cursor: 'pointer', flexShrink: 0 }}
          >
            <span
              style={{
                fontSize: 'var(--fs-xs)',
                fontWeight: 500,
                color: form.enabled ? 'var(--green-text)' : 'var(--text-muted)',
              }}
            >
              {form.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={form.enabled}
              onChange={(checked) => set('enabled', checked)}
            />
          </label>
        </div>
      </div>

      {/* Panel body: form fields */}
      <div className="panel-body col gap-4">
        <Field
          label="Latest version"
          hint={
            latestErr
              ? undefined
              : 'Newest store build — shows a dismissable "update available" prompt.'
          }
        >
          <Input
            className="mono"
            value={form.latestVersion}
            placeholder="2.2.0"
            disabled={!form.enabled}
            onChange={(e) => set('latestVersion', e.target.value)}
            onBlur={() => touch('latestVersion')}
            style={latestErr ? { borderColor: 'var(--red-solid)' } : undefined}
          />
          {latestErr && (
            <div
              className="row gap-1"
              style={{
                color: 'var(--red-text)',
                fontSize: 'var(--fs-xs)',
                marginTop: 5,
              }}
            >
              <Icon name="alert-circle" size={13} />
              {latestErr}
            </div>
          )}
        </Field>

        <Field
          label="Minimum version"
          hint={
            minErr
              ? undefined
              : 'Anything below is hard-gated (forced, non-dismissable update).'
          }
        >
          <Input
            className="mono"
            value={form.minimumVersion}
            placeholder="2.0.0"
            disabled={!form.enabled}
            onChange={(e) => set('minimumVersion', e.target.value)}
            onBlur={() => touch('minimumVersion')}
            style={minErr ? { borderColor: 'var(--red-solid)' } : undefined}
          />
          {minErr && (
            <div
              className="row gap-1"
              style={{
                color: 'var(--red-text)',
                fontSize: 'var(--fs-xs)',
                marginTop: 5,
              }}
            >
              <Icon name="alert-circle" size={13} />
              {minErr}
            </div>
          )}
        </Field>

        <Field label="Store URL">
          <Input
            value={form.storeUrl}
            placeholder="https://…"
            disabled={!form.enabled}
            onChange={(e) => set('storeUrl', e.target.value)}
          />
        </Field>

        <Field label="Release notes" hint="Shown in the update prompt body.">
          <Textarea
            value={form.releaseNotes}
            rows={4}
            disabled={!form.enabled}
            onChange={(e) => set('releaseNotes', e.target.value)}
          />
        </Field>
      </div>

      {/* Panel footer: actions */}
      <div
        style={{
          borderTop: '1px solid var(--border)',
          padding: '12px 14px',
          flexShrink: 0,
        }}
      >
        <div
          className="row"
          style={{ justifyContent: 'space-between', gap: 10 }}
        >
          <Button
            variant="secondary"
            size="sm"
            leftIcon="database"
            onClick={onLoadDefaults}
          >
            Load from constants
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon="save"
            loading={isLoading}
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

const AppVersions = () => {
  const { data, isLoading, error, refetch } = useGetAppVersionConfigsQuery();

  const byPlatform = (platform: Platform) =>
    data?.find((c) => c.platform === platform);

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      <PageHead
        title="App Versions"
        subtitle="Configure the update prompt shown to mobile users on launch."
      />
      <div className="grow scroll-y" style={{ padding: 22 }}>
        {isLoading ? (
          <LoadingBlock label="Loading version configuration…" />
        ) : error ? (
          <div style={{ maxWidth: 520, margin: '40px auto 0' }}>
            <EmptyState
              icon="alert-triangle"
              title="Couldn't load configuration"
              desc="The version constants service did not respond. Check the backend and try again."
              action={
                <Button variant="primary" leftIcon="refresh" onClick={refetch}>
                  Retry
                </Button>
              }
            />
          </div>
        ) : (
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{ marginBottom: 16, maxWidth: 760 }}>
              <Alert color="blue" icon="info" title="How the prompt behaves">
                Users below the minimum version are forced to update before
                continuing. Users between the minimum and latest see a
                dismissable prompt.
              </Alert>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 18,
                alignItems: 'stretch',
              }}
            >
              <PlatformCard
                platform={Platform.IOS}
                config={byPlatform(Platform.IOS)}
              />
              <PlatformCard
                platform={Platform.ANDROID}
                config={byPlatform(Platform.ANDROID)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppVersions;
